<template>
  <asc-page-loading-indicator
    :loading="
      (subscriptionsLoading || isSubscriptionDetailsLoading) &&
      !apiPageFailure.length
    "
  >
    <v-container v-if="!isSubscriptionCenterPageActive" fluid>
      <asc-page-coming-soon
        label="Subscription Center Page Coming Soon!"
        :title="getPageTitle"
      ></asc-page-coming-soon>
    </v-container>
    <v-container v-else fluid class="asc-subscription-center-details-container">
      <v-row no-gutters>
        <v-col cols="12" sm="12">
          <asc-page-header>
            <template #pageTitle>
              <asc-page-title :title="getPageTitle" />
            </template>
            <template #pageActions>
              <asc-subscription-center-header-wrapper
                :activate-enabled="isActiveEnabled"
                :save-disabled="isSaveDisabled"
                :cancel-disabled="isCancelDisabled"
                :save-loading="saveLoading"
                :active="isActive"
                @handleSave="handleSaveSubscription"
                @handleCancel="handleCancelChanges"
                @handleToggleActivate="handleSubscriptionActiveToggle"
              />
            </template>
          </asc-page-header>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12" sm="12">
          <asc-container>
            <template slot="navLink">
              <asc-app-link class="surfaceLink" to="/user/subscription-center"
                >&#60; Back</asc-app-link
              >
            </template>
            <template slot="header">
              <asc-surface-title :title="getSurfaceTitle"></asc-surface-title>
            </template>
            <template slot="subheader">
              <asc-surface-support-text
                :text="getDescription"
              ></asc-surface-support-text>
            </template>
            <asc-subscription-details-template
              :groups="getGroups"
              :subscription-details-loading="isSubscriptionDetailsLoading"
              :subscription-options="subscriptionOptionsData"
              :selection-label="getSelectionLabel"
              :single-airline-user="isSingleAirlineUser"
              :filter-options="getFilterOptions"
              :notifications="getNotifications"
              @onFilterTypeChange="handleFilterTypeChange"
              @onNotificationUpdate="handleNotificationUpdate"
              @onHeaderExpandAll="handleHeaderExpandAll"
              @onHeaderSelecUnselectAll="handleHeaderSelecUnselectAll"
              @onExpandCollapse="handleExpandCollapseOption"
              @onTableItemUpdate="handleSubscriptionOptionUpdate"
              @onUpdateUserSelections="handleSubscriptionOptionUpdate"
            >
            </asc-subscription-details-template>
          </asc-container>
        </v-col>
      </v-row>
    </v-container>
  </asc-page-loading-indicator>
</template>

<script>
import AscContainer from '@/components/atoms/AscContainer'
import AscPageHeader from '@/components/atoms/AscPageHeader'
import AscAppLink from '@/components/atoms/AscAppLink'

import AscPageTitle from '@/components/atoms/AscPageTitle'
import AscSurfaceTitle from '@/components/atoms/AscSurfaceTitle'
import AscSurfaceSupportText from '@/components/atoms/AscSurfaceSupportText'

import { AscSubscriptionDetailsTemplate } from '@/components/templates'
import { AscSubscriptionCenterHeaderWrapper } from '@/components/organisms'

import AscPageLoadingIndicator from '@/components/molecules/AscPageLoadingIndicator'
import AscPageComingSoon from '@/components/molecules/AscPageComingSoon'

import AppFeatureFlags from '@/utilities/appFeatureFlags'

import _ from 'lodash'

import { mapActions, mapState, mapGetters } from 'vuex'

import {
  USER_CONSTS as UC,
  SUBSCRIPTION_CENTER_CONSTS as SCC,
  ACMF_REPORT_LIST_CONSTS as ARLC,
  LOOKUP_CONSTS as LC,
} from '@/stores/consts'

import {
  getSubscriptionDetails,
  buildAirlinesToReportTypeItems,
  buildReportTypesToAirlineItems,
  buildAtachaptersToAirlineItems,
  buildAirlinesToAtaChapterItems,
  getSelections,
  getPWCUserReportTypeIDS,
} from '@/utilities/subscriptionCenter'

import { DEFAULT_ECCN_TEXT } from '@/utilities/appConsts'

export default {
  name: 'AscSubscriptionCenterDetailsContainer',

  components: {
    AscContainer,
    AscPageComingSoon,
    AscPageLoadingIndicator,
    AscSubscriptionDetailsTemplate,
    AscSubscriptionCenterHeaderWrapper,
    AscPageHeader,
    AscPageTitle,
    AscAppLink,
    AscSurfaceTitle,
    AscSurfaceSupportText,
  },

  props: {
    groupName: {
      type: String,
      default: '',
      required: true,
    },
  },

  data() {
    return {
      subscriptionCenterDetails: {},
      subscriptionOptionsData: [],
      selections: { filter: [], notifications: [] },
      savedSelections: { filter: [], notifications: [] },
      associatedEmailTypeIds: [],
      isSingleAirlineUser: false,
      isSubscriptionDetailsLoading: false,
      saveLoading: false,
      isActive: false,
    }
  },

  computed: {
    ...mapState(UC.NAMESPACE, ['profile', 'roles']),
    ...mapState(SCC.NAMESPACE, [
      'subscriptionsLoading',
      'analyticSystems',
      'subscriptions',
    ]),
    ...mapState({ apiPageFailure: 'apiPageFailure' }),
    ...mapGetters(UC.NAMESPACE, ['isPwcUser']),
    ...mapGetters(ARLC.NAMESPACE, [
      'mappedAcmfReportTypes',
      'mappedAcmfAirlinesWithMasterModels',
    ]),
    ...mapGetters(SCC.NAMESPACE, [
      'mappedAtaChapterGroupedWithAirlines',
      'mappedAirlineGroupedWithAtaChapter',
    ]),

    getFooterText() {
      return DEFAULT_ECCN_TEXT
    },

    isSubscriptionCenterPageActive() {
      return AppFeatureFlags.getSubscriptionCenter()
    },

    getPageTitle() {
      return this.subscriptionCenterDetails?.pageTitle || ''
    },

    getSurfaceTitle() {
      return this.subscriptionCenterDetails?.surfaceTitle || ''
    },

    getDescription() {
      return this.subscriptionCenterDetails?.description || ''
    },

    getFilterOptions() {
      return this.subscriptionCenterDetails?.filterOptions || []
    },

    getSelectedFilterOption() {
      if (this.getFilterOptions) {
        return (
          this.getFilterOptions.find(option => option.checked) ||
          this.getFilterOptions[0]
        )
      }
      return {}
    },

    getSelectionLabel() {
      return this.getSelectedFilterOption
        ? this.isSingleAirlineUser
          ? this.getSelectedFilterOption?.singleSelectionLabel
          : this.getSelectedFilterOption?.selectionLabel
        : ''
    },

    getGroups() {
      const gIndex = this.getFilterOptions.findIndex(
        option => option.groups && option.groups.length,
      )

      if (gIndex > -1) {
        const filterOptionWithGroup = this.getFilterOptions[gIndex]
        return {
          hasGroups: true,
          groups: filterOptionWithGroup.groups,
          groupValue: filterOptionWithGroup.value,
          selectedFilterOptionValue: this.getSelectedFilterOption.value,
        }
      }

      return {
        hasGroups: false,
      }
    },

    isValidSelections() {
      let isValid = true
      if (this.getFilterSelections.length) {
        this.getFilterSelections.forEach(selections => {
          const propertyNames = Object.getOwnPropertyNames(selections)
          propertyNames.forEach(name => {
            if (Array.isArray(selections[name]) && !selections[name].length)
              isValid = false
          })
        })
      } else {
        isValid = false
      }
      return isValid
    },

    getEnabledNotifications() {
      return (
        this.selections?.notifications.filter(
          subscription => subscription.isEnabled,
        ) || []
      )
    },

    getFilterSelections() {
      return this.selections?.filter || []
    },

    getSavedFilterSelections() {
      return this.savedSelections?.filter || []
    },

    getNotifications() {
      return this.selections?.notifications || []
    },

    getSavedNotifications() {
      return this.savedSelections?.notifications || []
    },

    hasFiltersChanged() {
      // Returns true if filterSelections is changed
      const filterSelections = this.getFilterSelections
      const savedFilterSelections = this.getSavedFilterSelections
      const isSameSize =
        _.size(filterSelections) === _.size(savedFilterSelections)

      return !(
        isSameSize &&
        _(filterSelections).xorWith(savedFilterSelections, _.isEqual).isEmpty()
      )
    },

    hasNotificationsChanged() {
      // Returns true if notifications is changed
      return !_(this.getNotifications)
        .differenceWith(this.getSavedNotifications, _.isEqual)
        .isEmpty()
    },

    isActiveEnabled() {
      if (this.groupName !== 'maintenance') {
        return this.isValidSelections
      } else {
        return this.isValidSelections && this.getEnabledNotifications.length > 0
      }
    },

    isSaveDisabled() {
      return (
        !(this.hasFiltersChanged || this.hasNotificationsChanged) &&
        !!this.getNotifications.length
      )
    },

    isCancelDisabled() {
      return !(this.hasFiltersChanged || this.hasNotificationsChanged)
    },
  },

  watch: {
    subscriptions(newSubs) {
      this.selections.notifications = []
      if (newSubs && newSubs.length) {
        // Pick the first subscription filter data as all notifications filter should be same within the same group
        if (newSubs[0].filter.length) {
          this.selections.filter = _.cloneDeep(newSubs[0].filter)
        }
        this.isActive = newSubs[0].isActive // need to is any is true here ?
      }

      // If no subscriptions returned then create new
      this.subscriptionCenterDetails?.notifications?.map(notification => {
        if (notification.emailTypeId) {
          let idx = -1
          if (newSubs && newSubs.length) {
            idx = newSubs.findIndex(
              sub => sub.emailTypeId === notification.emailTypeId,
            )
          }

          if (idx > -1) {
            this.selections.notifications.push({
              ...newSubs[idx],
              ...notification,
            })
          } else {
            this.selections.notifications.push(
              this.createNewSubscription(notification),
            )
          }
        }
        return notification
      })

      // Saved the original selections to revert by cancel action
      this.savedSelections = _.cloneDeep(this.selections)
    },
  },

  async created() {
    this.isSubscriptionDetailsLoading = true

    switch (this.groupName) {
      case 'acmf-report':
      case 'acmf-analytic-alert': {
        await Promise.all([
          this[ARLC.GET_ACMF_REPORT_TYPES](),
          this[ARLC.GET_ACMF_AIRLINES](),
          this[ARLC.GET_ACMF_MASTER_MODELS](),
        ])

        this.isSingleAirlineUser =
          this.mappedAcmfAirlinesWithMasterModels.length === 1

        break
      }
      case 'maintenance': {
        await this[SCC.GET_ANALYTIC_SYSTEMS]()
        await this[LC.GET_ATA_CHAPTERS]()

        this.isSingleAirlineUser =
          this.mappedAirlineGroupedWithAtaChapter.length === 1

        break
      }
      default:
        throw 'groupName not available!'
    }

    await getSubscriptionDetails(
      this.groupName,
      this.isSingleAirlineUser,
      this.roles.map(role => role.name),
    ).then(details => {
      this.subscriptionCenterDetails = details

      this.subscriptionCenterDetails?.notifications.forEach(notification => {
        if (notification.emailTypeId) {
          this.associatedEmailTypeIds.push(notification.emailTypeId)
        }
      })
    })

    // Get the associated notifications with subscription group
    await this[SCC.GET_SUBSCRIPTIONS](this.associatedEmailTypeIds)
    this.getSubscriptionsTableData()
    this.isSubscriptionDetailsLoading = false
  },

  methods: {
    ...mapActions(ARLC.NAMESPACE, [
      ARLC.GET_ACMF_REPORT_TYPES,
      ARLC.GET_ACMF_AIRLINES,
      ARLC.GET_ACMF_MASTER_MODELS,
    ]),

    ...mapActions(LC.NAMESPACE, [LC.GET_SYSTEMS, LC.GET_ATA_CHAPTERS]),

    ...mapActions(SCC.NAMESPACE, [
      SCC.GET_SUBSCRIPTIONS,
      SCC.SAVE_SUBSCRIPTIONS,
      SCC.GET_ANALYTIC_SYSTEMS,
    ]),

    handleFilterTypeChange(checkedOptionValue) {
      this.subscriptionCenterDetails?.filterOptions.map(filterOption => {
        if (filterOption.value === checkedOptionValue) {
          filterOption.checked = true
        } else {
          filterOption.checked = false
        }
        return filterOption
      })
      this.getSubscriptionsTableData()
    },

    handleNotificationUpdate(notification) {
      if (this.getNotifications.length) {
        const idx = this.selections?.notifications.findIndex(
          sub => sub.emailTypeId === notification.emailTypeId,
        )

        if (idx > -1) {
          this.selections.notifications.splice(idx, 1, notification)
        }

        // need to check if no notifications AND selections are made
        if (
          (this.selections.filter.length === 0 ||
            this.getEnabledNotifications.length === 0) &&
          this.isActive
        ) {
          this.handleSubscriptionActiveToggle(false)
        }
      }
    },

    handleHeaderExpandAll(isAllExpanded) {
      this.subscriptionOptionsData.map(
        subscriptionOption => (subscriptionOption.expanded = isAllExpanded),
      )
    },

    handleHeaderSelecUnselectAll(isAllChecked) {
      this.subscriptionOptionsData.map(subscriptionOption => {
        subscriptionOption.checked = isAllChecked
        subscriptionOption.items.map(
          subscriptionOptionItem =>
            (subscriptionOptionItem.checked = isAllChecked),
        )
        return subscriptionOption
      })
      this.handleOnUpdate(this.subscriptionOptionsData)
    },

    handleExpandCollapseOption(item, isExpanded) {
      const optionIndex = _.findIndex(this.subscriptionOptionsData, {
        value: item.value,
      })

      if (optionIndex > -1) {
        this.subscriptionOptionsData[optionIndex].expanded = isExpanded
      }
    },

    handleSubscriptionOptionUpdate(itemParent, item, checked) {
      // If itemParent is null then its an subscrition Option
      if (itemParent === null) {
        const optionIndex = _.findIndex(this.subscriptionOptionsData, {
          value: item.value,
        })

        if (optionIndex > -1) {
          const subscriptionOption = this.subscriptionOptionsData[optionIndex]
          subscriptionOption.checked = checked
          subscriptionOption.items.map(item => {
            item.checked = checked
            return item
          })
        }
      } else {
        // If itemParent is not null then its a subscrition Option items
        const itemParentIndex = _.findIndex(this.subscriptionOptionsData, {
          value: itemParent.value,
        })

        if (itemParentIndex > -1) {
          const subscriptionOption =
            this.subscriptionOptionsData[itemParentIndex]
          const items = subscriptionOption.items
          const itemIndex = _.findIndex(
            this.subscriptionOptionsData[itemParentIndex].items,
            {
              value: item.value,
            },
          )

          if (itemIndex > -1) {
            items[itemIndex].checked = checked
            const totalCheckedItems = _.countBy(items, {
              checked: true,
            })

            if (totalCheckedItems.true === items.length) {
              // All items are checked
              subscriptionOption.checked = true
              subscriptionOption.indeterminate = false
            } else if (
              totalCheckedItems.true !== 0 &&
              totalCheckedItems.true < items.length
            ) {
              // Only few items are checked
              subscriptionOption.indeterminate = true
              subscriptionOption.checked = undefined
            } else {
              // None of the items are checked
              subscriptionOption.checked = false
              subscriptionOption.indeterminate = false
            }
          }
        }
      }

      // On options update
      this.handleOnUpdate(this.subscriptionOptionsData)
    },

    handleSubscriptionActiveToggle(value) {
      this.isActive = value
      this.handleSaveSubscription()
    },

    getSubscriptionsTableData(filterSelections = this.getFilterSelections) {
      const selectedFilterOption = this.getSelectedFilterOption.value
      switch (this.groupName) {
        // Get ACMF subscriptions data
        case 'acmf-report': {
          const airlines = _.cloneDeep(this.mappedAcmfAirlinesWithMasterModels)
          let reportTypes = _.cloneDeep(this.mappedAcmfReportTypes)
          reportTypes = this.isPwcUser
            ? reportTypes.filter(f => getPWCUserReportTypeIDS.includes(f.value))
            : reportTypes
          if (selectedFilterOption === 0 || this.isSingleAirlineUser) {
            this.subscriptionOptionsData = buildAirlinesToReportTypeItems(
              reportTypes,
              airlines,
              filterSelections,
              this.getGroups.groups,
            )
          } else {
            this.subscriptionOptionsData = buildReportTypesToAirlineItems(
              airlines,
              reportTypes,
              filterSelections,
              this.getGroups.groups,
            )
          }
          break
        }
        case 'acmf-analytic-alert': {
          let airlines = _.cloneDeep(this.mappedAcmfAirlinesWithMasterModels)
          // for acmf analytic alert, filter the report types ONLY to those with active analytics
          let reportTypes = _.cloneDeep(this.mappedAcmfReportTypes).filter(
            reportTypeObj => {
              return reportTypeObj.hasAcmfAnalytic
            },
          )
          reportTypes = this.isPwcUser
            ? reportTypes.filter(f => getPWCUserReportTypeIDS.includes(f.value))
            : reportTypes
          if (selectedFilterOption === 0 || this.isSingleAirlineUser) {
            this.subscriptionOptionsData = buildAirlinesToReportTypeItems(
              reportTypes,
              airlines,
              filterSelections,
              this.getGroups.groups,
            )
          } else {
            this.subscriptionOptionsData = buildReportTypesToAirlineItems(
              airlines,
              reportTypes,
              filterSelections,
              this.getGroups.groups,
            )
          }
          break
        }
        // Get maintenance subscriptions data
        case 'maintenance': {
          let airlines = _.cloneDeep(
            this.mappedAirlineGroupedWithAtaChapter.map(m => {
              if (this.isPwcUser)
                m.items = m.items.filter(f => f.value === '49')
              return m
            }),
          )

          let analyticAtaChapters = this.isPwcUser
            ? _.cloneDeep(
                this.mappedAtaChapterGroupedWithAirlines.filter(
                  f => f.value === '49',
                ),
              )
            : _.cloneDeep(this.mappedAtaChapterGroupedWithAirlines)
          if (selectedFilterOption === 0 || this.isSingleAirlineUser) {
            this.subscriptionOptionsData = buildAirlinesToAtaChapterItems(
              airlines,
              analyticAtaChapters,
              filterSelections,
            )
          } else {
            this.subscriptionOptionsData = buildAtachaptersToAirlineItems(
              analyticAtaChapters,
              airlines,
              filterSelections,
            )
          }
          break
        }
        default: {
          this.subscriptionOptionsData = []
          break
        }
      }
    },

    handleCancelChanges() {
      // Revert selections with saved selections
      this.selections = _.cloneDeep(this.savedSelections)
      this.getSubscriptionsTableData()
    },

    handleOnUpdate(selections) {
      // Save Selections
      this.selections.filter = getSelections(
        this.groupName,
        this.getSelectedFilterOption,
        _.cloneDeep(selections),
      )
      if (
        (this.selections.filter.length === 0 ||
          this.getEnabledNotifications.length === 0) &&
        this.isActive
      ) {
        this.handleSubscriptionActiveToggle(false)
      }
    },

    async handleSaveSubscription() {
      if (this.getNotifications.length) {
        this.saveLoading = true

        // key is used for filter and it varies based on the groupName
        const key =
          this.groupName === 'maintenance' ? 'AtaChapters' : 'ReportNumbers'
        const subscriptionsToSave = this.selections?.notifications.map(
          subscription => {
            subscription.filter = this.getFilterSelections.filter(
              airlineReport => airlineReport[key].length,
            )
            subscription.isActive = this.isValidSelections
              ? this.isActive
              : false
            return subscription
          },
        )
        await this[SCC.SAVE_SUBSCRIPTIONS]({
          groupName: this.groupName,
          subscriptionsToSave: subscriptionsToSave,
          subscriptionIds: this.associatedEmailTypeIds,
        })

        // save previous selections so that it can be retrieved on cancel
        // However this gets overwritten once the subscriptions are updated by watcher
        this.savedSelections = _.cloneDeep(this.selections)
        this.saveLoading = false
      }
    },

    createNewSubscription(notification) {
      return {
        ...notification,
        ...{
          isActive:
            notification.visible !== undefined &&
            notification.visible === false,
          isEnabled:
            notification.visible !== undefined &&
            notification.visible === false,
          filter: [],
          isNew: true,
          frequency: 1,
        },
      }
    },
  },
}
</script>

<style lang="scss">
.asc-subscription-center-details-container {
  background-color: $gray-100;

  .asc-page-header {
    margin: 10px 0 8px 0;
  }

  .surfaceLink {
    a {
      color: $accent-700;
    }
  }

  .asc-surface--title {
    color: $primary-black;
    font-size: 14px;
    letter-spacing: 0;
    line-height: 18px;
    margin: 9px 0 21px 0;
    text-transform: uppercase;
    font-weight: normal;
  }
}
</style>
